<template>
	<form
		v-if="isActive && activeStep > 3"
		class="customer-info panel-body"
	>
		<div
			v-if="! isLoggedIn"
			class="text-center mt-4"
		>
			<a @click="login">Logga in</a> för att hämta uppgifterna från din profil
		</div>
		<div
			v-if="formVariation === 1"
			class="text-group"
		>
			<template v-for="(field, index) in formFields.standard">
				<input-text
					:key="'customer_form_' + index"
					v-model="internalValue[field.model]"
					:icon="field.icon"
					:submitError="errorStatus[field.name]"
					:placeholder="field.placeholder"
					:name="field.name"
					:type="field.type"
					:inputmode="field.inputmode"
					:pattern="field.pattern"
					:disabled="disabledElement(field.name)"
					:required="field.required"
					hideValid
					checkoutValidation
					@inputValid="setValidStatus"
				/>
				<div
					v-if="field.name === 'street'"
					:key="'customer_address_' + index"
					class="text-group-row"
				>
					<input-text
						v-for="(addressField, addressIndex) in field.zipAndCity"
						:key="'customer_address_' + addressIndex"
						v-model="internalValue[addressField.model]"
						:style="addressField.style"
						:icon="addressField.icon"
						:submitError="errorStatus[addressField.name]"
						:placeholder="addressField.placeholder"
						:name="addressField.name"
						:type="addressField.type"
						:inputmode="addressField.inputmode"
						:pattern="addressField.pattern"
						:disabled="disabledElement(addressField.name)"
						:required="addressField.required"
						hideValid
						checkoutValidation
						@inputValid="setValidStatus"
					/>
				</div>
			</template>
		</div>
		<div
			v-else-if="formVariation === 2"
			class="text-group"
		>
			<checkbox
				v-if="(selectedPaymentMethodId === 22 || [34, 35].includes(selectedPaymentMethodId)) && ! isCorporateCustomer"
				v-model="showSsn"
				small
				label="Visa personnummer"
			/>
			<template v-else-if="isCorporateCustomer">
				<div
					v-if="! corporateNumberEntered"
					class="input-group mt-4 input-field-button-group"
				>
					<input
						v-model="internalValue.corporate_number"
						class="form-control input-text"
						type="text"
						placeholder="Organisationsnummer"
						:disabled="disableCorporateNumberField"
						@keyup.enter="addCorporateNumber"
					>
					<text-button
						text="Hämta uppgifter"
						@click="addCorporateNumber"
					/>
				</div>
				<div
					v-else
					class="corporate-info"
				>
					<span class="sub-title">Företag</span>
					<h3>{{ corporateNameFromCollector }}</h3>
					<span class="corporate-number">{{ internalValue.corporate_number }}</span>
					<text-button
						size="tiny"
						text="Ändra"
						:disabled="disabledElement('element')"
						@click="corporateNumberEntered = false; disableCorporateNumberField = false; useOptionalInvoiceAddress = false; useOptionalDeliveryAddress"
					/>
				</div>
			</template>
			<div :class="['text-group', {'disabled': disabledElement('element')}]">
				<template v-if="isCorporateCustomer">
					<hr class="my-5">
					<h3 class="level-four-heading py-5">
						{{ selectedPaymentMethodId === 28 ? 'Faktureringsinformation' : 'Betalinformation' }}
					</h3>
				</template>
				<template v-for="(field, index) in formFields.standard">
					<input-text
						v-if="field.name === 'confirm-email'"
						:key="'customer_form_' + index"
						:value="internalConfirmedEmail"
						:icon="field.icon"
						:glyphicon="field.glyphicon"
						:submitError="errorStatus[field.name]"
						:placeholder="field.placeholder"
						:name="field.name"
						:type="field.type"
						:inputmode="field.inputmode"
						:pattern="field.pattern"
						:emailComparison="internalValue.email"
						:disabled="disabledElement(field.name)"
						:required="field.required"
						checkoutValidation
						hideValid
						@inputValid="setValidStatus"
					/>
					<input-text
						v-else
						:key="'customer_form_' + index"
						v-model="internalValue[field.model]"
						:icon="field.icon"
						:glyphicon="field.glyphicon"
						:submitError="errorStatus[field.name]"
						:placeholder="field.placeholder"
						:name="field.name"
						:type="field.type"
						:inputmode="field.inputmode"
						:pattern="field.pattern"
						:disabled="disabledElement(field.name)"
						:inputClasses="{ 'mask-input': ! showSsn && field.name === 'ssn'}"
						:required="field.required"
						hideValid
						checkoutValidation
						@inputValid="setValidStatus"
					/>
					<div
						v-if="field.name === 'street'"
						:key="'customer_address_' + index"
						class="text-group-row"
					>
						<input-text
							v-for="(addressField, addressIndex) in field.zipAndCity"
							:key="'customer_address_' + addressIndex"
							v-model="internalValue[addressField.model]"
							:style="addressField.style"
							:icon="addressField.icon"
							:glyphicon="addressField.glyphicon"
							:submitError="errorStatus[addressField.name]"
							:placeholder="addressField.placeholder"
							:name="addressField.name"
							:type="addressField.type"
							:inputmode="addressField.inputmode"
							:pattern="addressField.pattern"
							:disabled="disabledElement(addressField.name)"
							:required="addressField.required"
							hideValid
							checkoutValidation
							@inputValid="setValidStatus"
						/>
					</div>
				</template>
			</div>
			<template v-if="isCorporateCustomer && ! useOptionalInvoiceAddress">
				<dropdown-select
					v-model="invoiceDropdownIndex"
					:items="formattedCorporateAddressOptions"
					:disabled="disabledElement('element')"
					:placeholder="0"
					class="mt-3"
					fullWidth
					center
				/>
				<text-button
					class="my-5"
					variant="secondary"
					size="small"
					text="Ange annan adress"
					:disabled="disabledElement('element')"
					@click="useOptionalInvoiceAddress = true"
				/>
			</template>
			<template v-if="isCorporateCustomer && isStoreReservation === false">
				<div
					:class="['text-group', {'disabled': disabledElement('element')}]"
				>
					<hr class="my-5">
					<h3 class="level-four-heading py-5">
						Leveransinformation
					</h3>

					<template v-for="(field, index) in formFields.corporateDelivery">
						<input-text
							:key="'customer_form_' + index"
							v-model="internalValue[field.model]"
							:icon="field.icon"
							:glyphicon="field.glyphicon"
							:submitError="errorStatus[field.name]"
							:placeholder="field.placeholder"
							:name="field.name"
							:type="field.type"
							:inputmode="field.inputmode"
							:pattern="field.pattern"
							:disabled="disabledElement(field.name)"
							:required="field.required"
							hideValid
							checkoutValidation
							@inputValid="setValidStatus"
						/>
						<div
							v-if="field.name === 'delivery_street'"
							:key="'customer_address_' + index"
							class="text-group-row"
						>
							<input-text
								v-for="(addressField, addressIndex) in field.zipAndCity"
								:key="'customer_address_' + addressIndex"
								v-model="internalValue[addressField.model]"
								:style="addressField.style"
								:icon="addressField.icon"
								:glyphicon="addressField.glyphicon"
								:submitError="errorStatus[addressField.name]"
								:placeholder="addressField.placeholder"
								:name="addressField.name"
								:type="addressField.type"
								:inputmode="addressField.inputmode"
								:pattern="addressField.pattern"
								:disabled="disabledElement(addressField.name)"
								:required="addressField.required"
								hideValid
								checkoutValidation
								@inputValid="setValidStatus"
							/>
						</div>
					</template>
				</div>
				<template v-if="! useOptionalDeliveryAddress">
					<dropdown-select
						v-model="deliveryDropdownIndex"
						:items="formattedCorporateAddressOptions"
						:disabled="disabledElement('element')"
						:placeholder="0"
						class="mt-3"
						fullWidth
						center
					/>
					<text-button
						v-if="selectedPaymentMethodId !== 28"
						class="my-5"
						variant="secondary"
						size="small"
						text="Ange annan leveransadress"
						:disabled="disabledElement('element')"
						@click="useOptionalDeliveryAddress = true"
					/>
				</template>
			</template>
		</div>
	</form>
</template>

<script>
import { isEqual } from 'lodash';
import alert from 'alert';
import bus from 'eventbus';
import { mapState, mapGetters } from 'vuex';
import localSessionStorage from 'localSessionStorage';
import { customerFormData } from 'customerFormData';

const WEBHALLEN_DELBET = 22;
const WALLEY = 28;
const PAY_IN_STORE = 2;
const RESURS_BANK = [34, 35, 36];
const STORE_PICKUP = 1;
const SHIPPING = 2;

export default {
	props: {
		isActive: {
			type: Boolean,
			default: false,
		},
		isCorporateCustomer: {
			type: Boolean,
			default: false,
		},
		boughtInsurance: {
			type: Boolean,
			default: false,
		},
		customerInfoData: {
			type: Object,
			default () {
				return {
					ssn: '',
					first_name: '',
					last_name: '',
					email: '',
					intvoice_email: '',
					phone: '',
					zip_code: '',
					city: '',
					street_address: '',
					delivery_id: '',
					delivery_corporate_name: '',
					delivery_email: '',
					delivery_phone: '',
					delivery_zip_code: '',
					delivery_city: '',
					delivery_street_address: '',
					delivery_attention: '',
				};
			},
		},
		selectedStore: {},
		confirmedEmail: {},
		isStoreReservation: {},
		selectedPaymentMethodId: {},
		selectedShippingMethodId: {},
		activeStep: {
			type: Number,
		},
	},
	data () {
		return {
			showSsn: false,
			internalValue: null,
			corporateNumberEntered: false,
			useOptionalInvoiceAddress: false,
			useOptionalDeliveryAddress: false,
			selectedCorporateInvoiceAddressOption: false,
			selectedCorporateDeliveryAddressOption: false,
			disableCorporateNumberField: false,
			corporateAddressAvailableOptions: {},
			corporateNameFromCollector: null,
			invoiceAddressModal: false,
			corporateDeliveryAddressModal: false,
			validStatus: {},
			errorStatus: {},
			requiredArray: [],
			invalidArray: [],
			invoiceDropdownIndex: null,
			deliveryDropdownIndex: null,
			customerFormData,
			selectedDeliveryOption: null,
		};
	},
	mounted () {
		this.internalValue = JSON.parse(JSON.stringify(this.customerInfoData));
		this.$nextTick(() => {
			this.defaultCustomerInfo();
		});
		bus.$on('deliveryOptionChanged', this.setDeliveryOption);
	},
	methods: {
		setDeliveryOption (option) {
			this.selectedDeliveryOption = option;
		},
		login () {
			bus.$emit('header.show');
			bus.$emit('open:login');
		},
		defaultCustomerInfo () {
			const internalValue = this.internalValue;

			if (! this.isLoggedIn) { // @TODO i framtiden kanske detta ska sparas för inloggade med?
				let sessionValue = localSessionStorage.get('customerInfo');
				if (sessionValue) {
					sessionValue = JSON.parse(sessionValue);
				}
				if (! sessionValue) {
					sessionValue = {};
				}

				for (const key in sessionValue) {
					if (! internalValue[key]) {
						internalValue[key] = sessionValue[key];
					}
				}
			}

			const user = this.user;
			internalValue.ssn = internalValue.ssn || user.ssn || '';
			internalValue.first_name = internalValue.first_name || user.firstName || '';
			internalValue.last_name = internalValue.last_name || user.lastName || '';
			internalValue.email = internalValue.email || user.email || '';
			internalValue.phone = internalValue.phone || user.phoneNumber || '';
			internalValue.zip_code = internalValue.zip_code || user.zipCode || '';
			internalValue.city = internalValue.city || user.city || '';
			internalValue.street_address = internalValue.street_address || user.streetAddress || '';
			internalValue.corporate_name = internalValue.corporate_name || user.corporateName || '';
			internalValue.corporate_number = internalValue.corporate_number || user.corporateNumber || '';
			internalValue.invoice_email = internalValue.invoice_email || user.invoiceEmail || '';

			if (internalValue.corporate_number != '') {
				this.disableCorporateNumberField = true;
			}

			if (this.customerInfoData.zip_code !== this.internalValue.zip_code) {
				bus.$emit('update:zip', internalValue.zip_code);
			}
		},
		addCorporateNumber () {
			waja.get('collector/address-lookup/' + this.internalValue.corporate_number)
				.on('success', res => {
					this.corporateAddressAvailableOptions = res.data;

					this.selectCorporateInvoiceAddressOption(res.data[0]);
					this.selectCorporateDeliveryAddressOption(res.data[0]);

					this.corporateNameFromCollector = res.data[0].name;
					this.corporateNumberEntered = true;
				}).on('error', e => {
					alert('Hittar inga uppgifter om organisationsnumret.');
				}).go();
		},
		selectCorporateInvoiceAddressOption (option) {
			this.internalValue.corporate_name = option.name;
			this.internalValue.street_address = option.street_address;
			this.internalValue.zip_code = option.zip_code;
			this.internalValue.city = option.city;

			this.selectedCorporateInvoiceAddressOption = option;
			this.invoiceAddressModal = false;
		},
		selectCorporateDeliveryAddressOption (option) {
			this.internalValue.delivery_id = option.id;
			this.internalValue.delivery_corporate_name = option.name;
			this.internalValue.delivery_street_address = option.street_address;
			this.internalValue.delivery_zip_code = option.zip_code;
			this.internalValue.delivery_city = option.city;

			this.selectedCorporateDeliveryAddressOption = option;
			this.corporateDeliveryAddressModal = false;
		},
		// Takes names of required inputs and saves them in an array
		setRequiredArray (fields) {
			const nameArray = [];

			for (let i = 0; i < fields.standard.length; i++) {
				if (fields.standard[i].required) {
					nameArray.push(fields.standard[i].name);
				}
				if (fields.standard[i].name === 'street') {
					for (let j = 0; j < fields.standard[i].zipAndCity.length; j++) {
						if (fields.standard[i].zipAndCity[j].required) {
							nameArray.push(fields.standard[i].zipAndCity[j].name);
						}
					}
				}
			}

			for (let i = 0; i < fields.corporateDelivery.length; i++) {
				if (fields.corporateDelivery[i].required) {
					nameArray.push(fields.corporateDelivery[i].name);
				}
				if (fields.corporateDelivery[i].name === 'delivery_street') {
					for (let j = 0; j < fields.corporateDelivery[i].zipAndCity.length; j++) {
						if (fields.corporateDelivery[i].zipAndCity[j].required) {
							nameArray.push(fields.corporateDelivery[i].zipAndCity[j].name);
						}
					}
				}
			}

			this.requiredArray = nameArray;
		},
		// Takes data from input-text emit and sets to this.validStatus object to input name and valid status
		setValidStatus (to) {
			const newStatus = JSON.parse(JSON.stringify(this.validStatus));
			const name = to.name;
			const value = to.valid;
			newStatus[name] = value;
			if ( this.validStatus[name] === 'undefined' || this.validStatus[name] !== newStatus[name] ) {
				this.validStatus = newStatus;
			}
		},
		getErrorStatus () {
			const errorObj = {};
			this.requiredArray.forEach(item => {
				errorObj[item] = { status: false };
			});

			return errorObj;
		},
		// Puts the names of all input fields that are both required and invalid into an array
		setInvalidArray () {
			const invalidArray = [];
			for(let i = 0; i < this.requiredArray.length; i++) {
				const item = this.requiredArray[i];
				if (! this.validStatus[item]) {
					// Adds the names of all invalid fields to an array
					invalidArray.push(item);
				}
			}
			this.$emit('updateInvalidArray', invalidArray);
			return invalidArray;
		},
		disabledElement (element) {
			if (element === 'ssn') {
				return !! this.user.ssn;
			} else if (this.formVariation === 2 &&
				this.isCorporateCustomer &&
				! this.corporateNumberEntered &&
				element === 'element') {
				return true;
			} else {
				return false;
			}
		},
	},
	computed: {
		internalConfirmedEmail () {
			return this.confirmedEmail || this.user.email || '';
		},
		formVariation () {
			let formvariation = 0;

			if (this.selectedPaymentMethodId &&
				this.isStoreReservation === true &&
				this.selectedStore !== null &&
				this.selectedPaymentMethodId !== WALLEY) {
				formvariation = STORE_PICKUP;
			} else if (this.selectedPaymentMethodId &&
				((this.isStoreReservation === false &&
				this.selectedShippingMethodId !== null) ||
				this.selectedPaymentMethodId === WALLEY
				)) {
				formvariation = SHIPPING;
			}
			return formvariation;
		},
		formFields () {
			const fields = {
				standard: [],
				corporateDelivery: [],
			};

			if (this.formVariation === STORE_PICKUP) {
				if (! this.isCorporateCustomer &&
					(! RESURS_BANK.includes(this.selectedPaymentMethodId) &&
					this.selectedPaymentMethodId !== PAY_IN_STORE) ||
					this.boughtInsurance) {
					fields.standard = fields.standard.concat(this.customerFormData.fullName);
				} else if (this.isCorporateCustomer) {
					fields.standard = fields.standard.concat(this.customerFormData.corporateName);
				}

				if (this.boughtInsurance) {
					fields.standard = fields.standard.concat(this.customerFormData.address);
				}

				if (RESURS_BANK.includes(this.selectedPaymentMethodId) &&
					! this.isCorporateCustomer) {
					fields.standard = fields.standard.concat(this.customerFormData.ssn);
				}
				fields.standard = fields.standard.concat(this.customerFormData.contactInfo);
			} else if(this.formVariation === SHIPPING) {
				if (this.isCorporateCustomer) {
					fields.standard = fields.standard.concat(
						this.customerFormData.contactInfo,
						this.customerFormData.emailConfirmation,
					);

					if (this.selectedPaymentMethodId === WALLEY) {
						fields.standard = fields.standard.concat(this.customerFormData.invoiceDetails);
					}

					if (this.useOptionalInvoiceAddress) {
						fields.standard = fields.standard.concat(
							this.customerFormData.corporateName,
							this.customerFormData.address,
						);
					}

					if (! this.isStoreReservation) {
						fields.corporateDelivery = fields.corporateDelivery.concat(this.customerFormData.deliveryDetails);

						if (this.useOptionalDeliveryAddress) {
							fields.corporateDelivery = fields.corporateDelivery.concat(this.customerFormData.deliveryAddress);
						}
					}
				} else {
					if ((this.selectedPaymentMethodId !== PAY_IN_STORE) || this.boughtInsurance) {
						fields.standard = fields.standard.concat(this.customerFormData.fullName);
					}

					if (RESURS_BANK.includes(this.selectedPaymentMethodId)) {
						fields.standard = fields.standard.concat(this.customerFormData.ssn);
					}

					if (this.selectedPaymentMethodId !== WEBHALLEN_DELBET || this.boughtInsurance) {
						fields.standard = fields.standard.concat(this.customerFormData.address);
					}

					fields.standard = fields.standard.concat(
						this.customerFormData.contactInfo,
						this.customerFormData.emailConfirmation,
					);
				}
			}

			return fields;
		},
		formattedCorporateAddressOptions () {
			const options = [];

			if (this.corporateAddressAvailableOptions.length) {
				this.corporateAddressAvailableOptions.forEach((opt, index) => {
					const item = {
						label: opt.name,
						description: opt.street_address + ', ' + opt.zip_code + ' ' + opt.city,
						value: index,
					};
					options.push(item);
				});
			} else {
				const item = {
					label: 'Ange organisationsnummer..',
					description: '..så dyker adressen upp här',
					value: 0,
				};
				options.push(item);
			}
			return options;
		},
		...mapState ({
			user: state => state.user,
		}),
		...mapGetters ({
			isLoggedIn: 'user/isLoggedIn',
		}),
	},
	watch: {
		internalValue: {
			handler (ci) {
				/* Removes all whitespace from emails */
				this.internalValue.email = this.internalValue.email.replace(/\s/g, '');
				this.$emit('update:customerInfoData', ci);

				if (! this.isLoggedIn && ! localSessionStorage.get('isCustomerTerminal')) {
					const jsonData = JSON.stringify(ci);
					localSessionStorage.set('customerInfo', jsonData);
				}
			},
			deep: true,
		},
		customerInfo: {
			handler (ci) {
				if (! isEqual(ci, this.internalValue)) {
					this.internalValue = JSON.parse(JSON.stringify(ci));
				}
			},
			deep: true,
		},
		requiredArray () {
			this.errorStatus = this.getErrorStatus();
			this.invalidArray = this.setInvalidArray();
		},
		validStatus () {
			this.invalidArray = this.setInvalidArray();
		},
		user: {
			handler (user) {
				if (this.isLoggedIn && user) {
					this.defaultCustomerInfo();
				}
			},
			deep: true,
		},
		formFields (fields) {
			this.setRequiredArray(fields);
		},
		invoiceDropdownIndex (index) {
			this.selectCorporateInvoiceAddressOption(this.corporateAddressAvailableOptions[index]);
		},
		deliveryDropdownIndex (index) {
			this.selectCorporateDeliveryAddressOption(this.corporateAddressAvailableOptions[index]);
		},
	},
};
</script>
